import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from '../ui/textarea';
import { SingleUserCombobox } from "../ui/SingleUserCombobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useTenancy } from '@/contexts/TenancyContext';
import { AuditRegister } from '@/js/external-js/variableModules/namespaces/AuditRegister.namespace';
import { EntityLinkedDocumentsSection } from './EntityLinkedDocumentsSection';

export function FindingCreateDialog({ open, onOpenChange, onCreate, auditOptions, defaultAuditId, inheritedControls }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // DED-973: Updated to include linkedDocuments
  onCreate: (finding: { summary: string; owner: string; status: "Open" | "Closed"; type: string; auditId?: string; comments?: string; linkedDocuments?: string[] }) => void | string | boolean | Promise<void | string | boolean>;
  auditOptions: { id: string; label: string }[];
  defaultAuditId?: string;
  // DED-973: Controls to inherit from parent audit
  inheritedControls?: string[];
}) {
  const { activeTenancy } = useTenancy();
  const [auditSettings, setAuditSettings] = useState<{ findingTypes: string[]; riskCategories: string[] }>({
    findingTypes: [],
    riskCategories: []
  });
  const [auditLoading, setAuditLoading] = useState(true);
  const [summary, setSummary] = useState("");
  const [owner, setOwner] = useState("");
  const [status, setStatus] = useState<"Open" | "Closed">("Open");
  const [type, setType] = useState<string>("");
  const [auditId, setAuditId] = useState<string | undefined>(defaultAuditId);
  const [comments, setComments] = useState("");
  // DED-973: State for linked documents
  const [linkedDocuments, setLinkedDocuments] = useState<string[]>([]);
  
  // Load audit settings when dialog opens
  React.useEffect(() => {
    if (open && activeTenancy?.id) {
      setAuditLoading(true);
      AuditRegister.Audit.Menu(activeTenancy.id)
        .then(menuData => {
          if (menuData) {
            setAuditSettings({
              findingTypes: menuData.findingTypes || [],
              riskCategories: menuData.riskCategories || []
            });
            // Set default type if not already set
            if (!type && menuData.findingTypes?.length > 0) {
              setType(menuData.findingTypes[0]);
            }
          }
        })
        .catch(error => {
          console.error('Error loading audit settings:', error);
        })
        .finally(() => {
          setAuditLoading(false);
        });
    }
  }, [open, activeTenancy?.id, type]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">Create New Finding / Non-Conformity</DialogTitle>
          </div>
        <div className="flex flex-col gap-2 max-w-full overflow-hidden px-4 pb-4">
          <label className="text-xs font-medium">Type</label>
          <Select value={type} onValueChange={(value: string) => setType(value)} disabled={auditLoading}>
            <SelectTrigger className="border rounded px-2 py-1 text-xs w-full">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {auditSettings.findingTypes?.map((findingType: string) => (
                <SelectItem key={findingType} value={findingType}>
                  {findingType}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="text-xs font-medium">Summary</label>
          <Textarea className="border rounded px-2 py-1" value={summary} onChange={e => setSummary(e.target.value)} />
          <label className="text-xs font-medium">Owner</label>
          <div className="max-w-full overflow-hidden">
            <div className="w-full max-w-full overflow-hidden">
              <SingleUserCombobox
                selected={owner}
                setSelected={setOwner}
                placeholder="Select owner"
                className="max-w-full"
              />
            </div>
          </div>
          <label className="text-xs font-medium">Status</label>
          <Select value={status} onValueChange={(value: "Open" | "Closed") => setStatus(value)}>
            <SelectTrigger className="border rounded px-2 py-1 text-xs w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="Closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <label className="text-xs font-medium">Link to Audit</label>
          <Select value={auditId ?? "None"} onValueChange={val => setAuditId(val === "None" ? undefined : val)}>
            <SelectTrigger className="border rounded px-2 py-1 text-xs w-full">
              <SelectValue placeholder="Select audit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="None">None</SelectItem>
              {auditOptions.map(opt => (
                <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="text-xs font-medium">Comments</label>
          <Textarea className="border rounded px-2 py-1" value={comments} onChange={e => setComments(e.target.value)} />
          
          {/* DED-973: Linked Documents Section */}
          <div className="mt-4 pt-4 border-t">
            <EntityLinkedDocumentsSection
              entityType="Finding"
              linkedDocuments={linkedDocuments}
              onDocumentsChange={setLinkedDocuments}
              inheritedControls={inheritedControls || []}
              entityTitle={summary || 'New Finding'}
            />
          </div>
        </div>
        <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            onClick={async () => {
              // DED-973: Include linkedDocuments in the finding data
              await onCreate({ summary, owner, status, type, auditId, comments, linkedDocuments });
              onOpenChange(false);
              setSummary("");
              setOwner("");
              setStatus("Open");
              setType(auditSettings.findingTypes?.[0] || "");
              setAuditId(defaultAuditId);
              setComments("");
              setLinkedDocuments([]);
            }}
            disabled={!summary || !owner}
          >
            Create
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
